import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography, Grid, debounce, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Stack } from "@mui/material";
import CustomSelectInput from "../commoncomponents/CustomSelectInput";
import CustomTextField from "../commoncomponents/CustomTextField";
import CustomTextArea from "../commoncomponents/CustomTextArea";
import ComponentHeader from "../commoncomponents/ComponentHeader";
import CustomTextFieldWithSearch from "../commoncomponents/CustomTextFieldWithSearch";
import { useDispatch, useSelector } from "react-redux";
import CustomDateTimePicker from "../commoncomponents/CustomDateTimePicker";
import { decryptDataForHyperlink, updateAssessmentsPartsInitialState, updateAssessmentsPartsRequired, updateOutsourceDetails, updateWorkOrderStatus } from "../../redux/actions/workOrderActionTypes";
import { getAuthToken, getCreatedBy, getFacilityId } from "../../utility/tokenUtility";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import PageLoader from "../commoncomponents/PageLoader";
import CustomAlert from "../userlocationcomponents/CustomAlert";
import CustomDialog from "../commoncomponents/CustomDialog";
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CustomAgGrid from "../commoncomponents/CustomAgGrid";
import RoutineInspectionBottomButtons from "./RoutineInspectionBottomButtons";
import riRepository from "../../api/repositories/riRepository";
import { updateBasicDetailsRoutineInspection } from "../../redux/actions/routineInspectionActions";
import { mandatoryFields } from "../../redux/actions/assetActions";
import { useAccess } from "../../AccessContext";
import utilities from "../../utility/utilities";
import CustomDatePicker from "../commoncomponents/CustomDatePicker";
import { statusList } from "../../utility/message";

export const CancelWorkOrder = ({ open, onClose, onConfirm }) => {
    const [value, setValue] = useState({
        cancelledBy: "",
        reasonforCancellation: "",
    });

    const [errors, setErrors] = useState({
        cancelledBy: false,
        reasonforCancellation: false,
    });

    const [errorMessage, setErrorMessage] = useState({
        cancelledBy: "",
        reasonforCancellation: "",
    });

    const handleChange = (value, name) => {
        setValue((previous) => ({
            ...previous,
            [name]: value,
        }));

        if (value.trim() !== "") {
            setErrors((prevErrors) => ({
                ...prevErrors,
                [name]: false,
            }));

            setErrorMessage((prevMessages) => ({
                ...prevMessages,
                [name]: "",
            }));
        }
    };

    const handleConfirm = () => {
        const newErrors = {
            cancelledBy: value.cancelledBy.trim() === "",
            reasonforCancellation: value.reasonforCancellation.trim() === "",
        };

        const newErrorMessages = {
            cancelledBy: value.cancelledBy.trim() === "" ? "Cancelled By is required" : "",
            reasonforCancellation: value.reasonforCancellation.trim() === "" ? "Reason for Cancellation is required" : "",
        };

        setErrors(newErrors);
        setErrorMessage(newErrorMessages);

        const isValid = !newErrors.cancelledBy && !newErrors.reasonforCancellation;

        if (isValid) {
            onConfirm(value);
            onClose('yes');
        }
    };

    const handleClose = () => {
        onClose('no');
        setValue({
            cancelledBy: "",
            reasonforCancellation: "",
        });

        setErrorMessage({
            cancelledBy: "",
            reasonforCancellation: "",
        });

        setErrors({
            cancelledBy: false,
            reasonforCancellation: false,
        });

    };

    useEffect(() => {
        if (open) {
            setErrors({
                cancelledBy: false,
                reasonforCancellation: false,
            });
            setErrorMessage({
                cancelledBy: "",
                reasonforCancellation: "",
            });
            setValue({
                cancelledBy: "",
                reasonforCancellation: "",
            })
        }
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Cancel WorkOrder</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={6} md={6}>
                        <CustomTextField
                            label="Cancelled By"
                            id="cancelledBy"
                            name='cancelledBy'
                            value={value?.cancelledBy}
                            onChange={(e) => handleChange(e, 'cancelledBy')}
                            isMandatoryField={true}
                            error={errors.cancelledBy}
                            errorMessage={errorMessage.cancelledBy}
                        />
                    </Grid>
                    <Grid item xs={6} md={6}>
                        <CustomTextField
                            label="Reason for Cancellation"
                            id="reasonforCancellation"
                            name="reasonforCancellation"
                            value={value?.reasonforCancellation}
                            onChange={(e) => handleChange(e, 'reasonforCancellation')}
                            isMandatoryField={true}
                            error={errors.reasonforCancellation}
                            errorMessage={errorMessage.reasonforCancellation}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary">No</Button>
                <Button onClick={handleConfirm} color="error">Yes</Button>
            </DialogActions>
        </Dialog>
    );
};


const RoutineInspectionBasicDetails = ({ setPage, page, buttonAttributes }) => {

    const findHeaderContent = buttonAttributes.find(
        (item) => item.currentPageName === page
    );

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
  
    const hyperLinkDecryptData = useSelector((state)=>state?.hyperLinkData)
        
          // Function to import the key from a Base64 string
     async function importKey(base64Key) {
         const rawKey = base64ToArrayBuffer(base64Key);
         return await crypto.subtle.importKey('raw', rawKey, 'AES-GCM', true, ['decrypt']);
     }
     
     // Function to convert Base64 string to ArrayBuffer
     function base64ToArrayBuffer(base64) {
         const binaryString = window.atob(base64);
         const len = binaryString.length;
         const bytes = new Uint8Array(len);
         for (let i = 0; i < len; i++) {
             bytes[i] = binaryString.charCodeAt(i);
         }
         return bytes.buffer;
     }
     
     // Function to decrypt data using AES-GCM
     async function decryptDataKey(key, encryptedData, iv) {
         const decrypted = await crypto.subtle.decrypt(
             {
                 name: "AES-GCM",
                 iv: iv,
             },
             key,
             encryptedData
         );
     
         const decoder = new TextDecoder();
         return JSON.parse(decoder.decode(decrypted));
     }
     
     // Retrieve the encrypted state, IV, and key from the URL
     const queryParams = new URLSearchParams(location?.search);
     const encryptedStateString = queryParams?.get("state");
     const ivString = queryParams?.get("iv");
     const keyString = queryParams?.get("key");
     
     const getDecryptData = () =>{
         try {
             if (encryptedStateString && ivString && keyString) {
                setLoading(true);
                 // Convert the Base64 encoded data, IV, and key back to ArrayBuffer
                 const encryptedData = base64ToArrayBuffer(decodeURIComponent(encryptedStateString));
                 const iv = base64ToArrayBuffer(decodeURIComponent(ivString));
             
                 // Import the encryption key
                 importKey(decodeURIComponent(keyString)).then((key) => {
                     // Decrypt the data
                     decryptDataKey(key, encryptedData, iv).then((decryptedData) => {
                         dispatch(decryptDataForHyperlink("DECRYPT_DATA_REDUCERS_HYPERLINK",decryptedData))
                     }).catch((error) => {
                         console.error("Decryption failed:", error);
                     });
                 });
                //  setLoading(false);
             } else {
                 console.log("Encrypted state, IV or key not found in the URL");
             }
         } catch (error) {
             console.log("Decryption failed:", error)
         }
     }
     
    const basicDetails = useSelector((state) => state.routineInspecBasicdetails);
    const mandatoryBasicDetails = useSelector((state) => state.routineInspecMandatoryFields.mandatoryFieldsBasicDetails);
    const assetCategoryType = useSelector((state) => state?.serviceType);
    const getStepper = useSelector((state) => state?.checkParts);
    const status = useSelector((state) => state?.workOrderStatus?.status);
    const processingPercentage = useSelector((state) => state?.workOrderStatus?.percentage);
    const editServiceType = location.state?.service  || hyperLinkDecryptData?.service;
    const viewmode = location.state?.viewmode;
    const workorderCategoryType = location.state?.category || hyperLinkDecryptData?.category;
    const targetResponse = [{ id: 1, label: "120 Mins", }, { id: 2, label: "15 Mins", }];
    const serviceType = assetCategoryType?.assetType === "fem" ? "fem" : "bem";
    const categoryType = assetCategoryType?.category || "";
    const setScheduledField = categoryType === "Scheduled" ? true : false;
    const accessData = useAccess();
    const access = utilities.getScreenPermissions(accessData, "ENGT001");


    const [basicDetailsdata, setBasicDetailsData] = useState(basicDetails || {});
    const [isAssetNoSelected, setisAssetNoSelected] = useState(false);
    const [isLocationCodeSelecetd, setisLocationCodeSelecetd] = useState(false);
    const [openAutoAsignDialog, setOpenAutoAsignDialog] = useState(false);
    const [confirmationAutoAsign, setConfirmationAutoAsign] = useState(false);
    const [enable, setEnable] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [pageNavigate, setPageNavigate] = useState(false);
    const [checkAutoAsign, setCheckAutoAsign] = useState(false);
    const [errorDialog, setErrorDialog] = useState(false);
    const [errorDialogMssg, setErrorDialogMssg] = useState("");
    const [error, setError] = useState({
        workorderDateandTime: false,
        requestor: false,
        designation: false,
        assetNo: false,
        requestDetails: false,
        recivedDateandTime: false,
        locationCode:false
    });
    const [errorMessage, setErrorMessage] = useState({
        workorderDateandTime: "",
        requestor: "",
        designation: "",
        assetNo: "",
        requestDetails: "",
        recivedDateandTime: "",
        locationCode:""
    });
    const [basicDetailsOptions, setBasicDetailsOptions] = useState(
        {
            workRequestNo: [],
            assetNo: [],
            type: [],
            priority: [],
            category: [],
            locationCode:[]
        }
    );

    const statusMapping = {
        "Open": 1,
        "Scheduled":2,
        "In Progress": 3,
        "Completed": 4,
        "Closed": 5,
        "Cancelled": 6,
        "Verified":8,
        "Not Done and Closed":7,
        "Completed-Outsource":9,
    };

    // Map the current status to the corresponding number
    const mappedStatus = statusMapping[status] || null; // If status doesn't exist, fallback to null or any default value
    const checkcancelWOStatus = status === "Open" || status === "In Progress" || status === "Cancelled" || status === "In Progress-Outsource"? true : false;

    const serviceName = basicDetailsdata?.referenceWorkRequestNo?.serviceName === "FEMS" ? "fem" : "bem" ?
        basicDetailsdata?.assetNo?.serviceName === "FEMS" ? "fem" : "bem" : '';


    const handleOnchange = (value, name, errorStateName) => {
        if (name === "requestDetails") {
            if (value?.startsWith(" ")) {
                setError((prev) => ({
                    ...prev,
                    [name]: true,
                }));
                setErrorMessage((prev) => ({
                    ...prev,
                    [name]: `${errorStateName} should not start with spaces`,
                }));
                return;
            } else if (value?.endsWith("  ")) {
                setError((prev) => ({
                    ...prev,
                    [name]: true,
                }));
                setErrorMessage((prev) => ({
                    ...prev,
                    [name]: `${errorStateName} should not end with spaces`,
                }));
                return;
            } else {
                setError((prev) => ({
                    ...prev,
                    [name]: false,
                }));
                setErrorMessage((prev) => ({
                    ...prev,
                    [name]: "",
                }));
            }
        }

        if (name === 'contactNo') {
            const regex = /[^0-9+\-()/]/g;
            value = value.replace(regex, ''); // Filter the value to allow only numbers, +, and -
        }


        setBasicDetailsData((previous) => {
            const updatedData = {
                ...previous,
                [name]: value,
            };

            if (name === "assetNo" && value?.id) {
                updatedData.assetNo = { ...value, label: value?.assetNo || "" };
                updatedData.oldAssetNo = value?.oldAssetNo || "";
                updatedData.underwarrenty = value?.underwarrenty || "";
                updatedData.description = value?.assetdesc || "";
                updatedData.workgroupDescription = value?.workGroupcodeDesc || "";
                updatedData.locationCode = value?.locationCode || "";
                updatedData.locationDesc = value?.locationDesc || "";
                updatedData.deptDesc = value?.deptDesc || "";
                setisAssetNoSelected(true);
            } else if (name === "assetNo") {
                updatedData.assetNo = "";
                updatedData.oldAssetNo = "";
                updatedData.underwarrenty = "";
                updatedData.description = "";
                updatedData.workgroupDescription = "";
                updatedData.locationCode = "";
                updatedData.locationDesc = "";
                updatedData.deptDesc = "";
                setisAssetNoSelected(false);
            }
    
            if (name === "locationCode" && value?.id) {
                updatedData.locationCode = { ...value, label: value?.locationCode || "" };
                updatedData.locationDesc = value?.locationDesc || "";
                updatedData.assetNo = { id: value?.id || "", label: value?.assetNo || "" };
                updatedData.oldAssetNo = value?.oldAssetNo || "";
                updatedData.underwarrenty = value?.underwarrenty || "";
                updatedData.description = value?.assetdesc || "";
                updatedData.workgroupDescription = value?.workGroupcodeDesc || "";
                updatedData.deptDesc = value?.deptDesc || "";
                setisLocationCodeSelecetd(true);
            } else if (name === "locationCode") {
                updatedData.locationCode = "";
                updatedData.locationDesc = "";
                updatedData.assetNo = {id:"",label:""};
                updatedData.oldAssetNo = "";
                updatedData.underwarrenty = "";
                updatedData.description = "";
                updatedData.workgroupDescription = "";
                updatedData.deptDesc = "";
                setisLocationCodeSelecetd(false);
            }

            return updatedData;
        });

        setError((prev) => {
            const updatedError = {
                ...prev,
                [name]: false
            };
            return updatedError;
        });

        setErrorMessage((prev) => ({
            ...prev,
            [name]: ''
        }));
    };

    const memoizedDetails = useMemo(() => ({
        workorderDateandTime: basicDetailsdata?.workorderDateandTime || '',
        requestDetails: basicDetailsdata?.requestDetails || '',
        // locationCode:basicDetailsdata?.locationCode || ""
    }), [
        basicDetailsdata?.workorderDateandTime,
        basicDetailsdata?.requestDetails,
        // basicDetailsdata?.locationCode
    ]);
    

    //function to check the mandatory field are filled or not(responsible for green ticks on left side)
    const checkMandatoryValueFilled = useCallback(debounce((
        workorderDateandTime,
        requestDetails,
        // locationCode
    ) => {
        const updateStatus = (type, value, name, isVisible, errorState) => ({
            type,
            valueFilled: !errorState && !!value,
            name,
            isVisible
        });
        const updateDateandTimeBasicDetails = updateStatus("Date and Time", workorderDateandTime, 'workorderDateandTime', true, error?.workorderDateandTime);
        const updaterequestDetails = updateStatus("Request Details", requestDetails, 'requestDetails', true, error?.requestDetails);
        // const updateLocationCode = updateStatus("Location Code", locationCode, 'locationCode', !isAssetNoSelected, error?.locationCode);

        const arrayAfterUpdating = [
            updateDateandTimeBasicDetails,
            updaterequestDetails,
        ];

        if (arrayAfterUpdating !== undefined && arrayAfterUpdating.every(item => item !== undefined)) {
            dispatch({ type: "UPDATE_ROUTINE_INSPECTION_BASIC_DETAILS_MANDATORY_FIELDS", payload: arrayAfterUpdating })
        };
    }), [dispatch, error]);

    useEffect(() => {
        const {
            workorderDateandTime,
            requestDetails,
            // locationCode
        } = memoizedDetails;
        checkMandatoryValueFilled(
            workorderDateandTime,
            requestDetails?.trim(),
            // locationCode?.id
        );
    }, [
        memoizedDetails,
        checkMandatoryValueFilled
    ], []);

    const handleValidationForRequiredFields = () => {
        let isValid = true;
        mandatoryBasicDetails.map((item) => {
            if (item.valueFilled === false) {
                setError((prev) => ({
                    ...prev,
                    [item.name]: true,
                }));
                setErrorMessage((prev) => ({
                    ...prev,
                    [item?.name]: prev[item?.name] || `${item?.type} is required `,
                }));
                isValid = false;
            }
        });
        return isValid;
    };

    const assetNoOptions = async () => {
        let requestNoService = basicDetailsdata?.referenceWorkRequestNo?.serviceName || "";
        let service = serviceType === "fem" ? "FEMS" : "BEMS";
        let servicecategory = editServiceType ? editServiceType : requestNoService || service

        const assetNo = await riRepository.fetchAssetNobyService(servicecategory)
        setBasicDetailsOptions((prevOptions) => ({
            ...prevOptions,
            assetNo: assetNo?.map((item, index) => {
                return {
                    key: `option${index}`,
                    id: item?.assetId || "",
                    label: `${item?.assetdescno || ""} - ${item?.assetdesc || ""}` || "",
                    assetNo: item?.assetdescno || "",
                    locationCode: item?.locationcode || "",
                    locationDesc:item?.locationdesc || "",
                    deptDesc:item?.deptDesc || "",
                    underwarrenty: item?.isunderwarranty === true
                        ? "Yes"
                        : item?.isunderwarranty === false
                            ? "No"
                            : "",
                    assetdesc: item?.assetdesc || "",
                    workGroupId: item?.wgId || "",
                    workGroupcodeDesc: `${(item?.wgId && item?.wgcode) ? item?.wgcode : ""}${(item?.wgcode && item?.wgdesc) ? " - " : ""}${(item?.wgId && item?.wgdesc) ? item?.wgdesc : ""}` || "",
                    oldAssetNo: item?.oldassetno || "",
                    serviceId: item?.serviceId || "",
                    serviceName: item?.serviceName || ""
                };

            })

        }));
    };

    const locationCodeOptions = async () => {
        const locationCode = await riRepository.fetchLocationCode();
        setBasicDetailsOptions((prevOptions) => ({
            ...prevOptions,
            locationCode: locationCode?.items?.map((item, index) => {
                return {
                    key: `option${index}`,
                    id: item?.id || "",
                    label: `${item?.locationcode || ""} - ${item?.locationdesc || ""}` || "",
                    deptCode:item?.departmentcode || "",
                    deptDesc:item?.departmentdesc || "",
                    locationCode:item?.locationcode || "",
                    locationDesc:item?.locationdesc || ""
                };
            })
        }));

    };

    const categoryandPriorityOptions = async () => {
        const categoryandPriority = await riRepository.lovOptions()
        setBasicDetailsOptions((prevOptions) => ({
            ...prevOptions,
            priority: categoryandPriority?.femworkorderpriority?.map((item) => ({
                key: `option${item?.id}`,
                label: item?.workorderpriority,
                isvisible: item?.isvisible,
                id: item?.id
            })),

            category: categoryandPriority?.femworkordercategory?.map((item) => ({
                key: `option${item?.id}`,
                label: item?.workordercategory,
                isvisible: item?.isvisible,
                id: item?.id
            })),
        }));

    };

    useEffect(() => {
        dispatch({
            type: "SERVICE&CATEGORY_TYPE", payload: {
                assetType: editServiceType ? editServiceType : serviceName,
                category: workorderCategoryType ? workorderCategoryType : 'Unscheduled'
            }
        })
    }, [basicDetailsdata?.referenceWorkRequestNo, basicDetailsdata?.assetNo, basicDetailsdata?.category, categoryType, workorderCategoryType, editServiceType, setScheduledField])


    const fetchData = async () => {
        try {
            if (location?.state?.id || hyperLinkDecryptData?.id || "") {
                const response = await riRepository.getWorkOrderSummary(editServiceType, location?.state?.id || hyperLinkDecryptData?.id);

                if (editServiceType === 'fem') {
                        const data = response?.[0];
                        const status = data?.femroutineinspectionheaders[0]?.femworkorderstatus?.workorderstatus || "";
                        const percentageStatus = data?.femroutineinspectionheaders[0]?.processingpercentage || "";
                        const getRepairtype = data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.femworespondtype?.respondtype ? data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.femworespondtype?.respondtype : data?.femworespondtype || ''
                        const setvendorField = getRepairtype === 'Outsource' ? true : false;
                        dispatch(updateWorkOrderStatus("STATUS_CHANGES", { status: status, percentage: percentageStatus }));

                        const employeeResponse = await riRepository.fetchEmployeeDetails();
                        const lovOptionsResponse = await riRepository.lovOptions();
                        const vendorDetails = await riRepository.fetchVendorDetails();

                        const mapEmployeeIdToName = (id) => {
                            const employee = employeeResponse?.find(emp => emp?.id === id);
                            return employee ? employee?.employeename : '';
                        };

                        const mapEmployeeIdTodesignation = (id) => {
                            const employee = employeeResponse?.find(emp => emp?.id === id);
                            return employee ? employee?.designation : '';
                        };

                        const mapEmployeeIdToCode = (id) => {
                            const employee = employeeResponse?.find(emp => emp?.id === id);
                            return employee ? employee?.employeecode : '';

                        };

                        const maptoVendorDetails = (id) => {
                            const vendor = vendorDetails?.find(v => v?.id === id);
                            return vendor ? vendor?.vendorname : '';
                        };

                        const maptoVendorCode = (id) => {
                            const vendor = vendorDetails?.find(v => v?.id === id);
                            return vendor ? vendor?.vendorcode : '';
                        };

                        const mapFlagIdToLabel = (id) => {
                            const flag = lovOptionsResponse?.femschworepairflags?.find(f => f?.id === id);
                            return flag ? flag?.repairflag : '';
                        };

                        const mapPicIdToLabel = (id) => {
                            const pic = lovOptionsResponse?.femschworepairpics?.find(p => p?.id === id);
                            return pic ? pic?.repairpic : '';
                        };

                        const mapActivityIdToLabel = (id) => {
                            const activity = lovOptionsResponse?.femschworepairactivities?.find(a => a?.id === id);
                            return activity ? activity?.repairactivity : '';
                        };

                        // Mapping repairTracker data
                        const repairTracker = data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.femroutineinspectrepairdetails?.map((repairDetail) => {
                            return {
                                employeename: {
                                    id: repairDetail?.employeeid || "",
                                    employeename: mapEmployeeIdToName(repairDetail?.employeeid) || "",
                                    employeecode: mapEmployeeIdToCode(repairDetail?.employeeid) || ""
                                },
                                flag: {
                                    id: repairDetail?.femuschworepairflagId || "",
                                    label: mapFlagIdToLabel(repairDetail?.femuschworepairflagId) || ""
                                },
                                pic: {
                                    id: repairDetail?.femuschworepairpicId || "",
                                    label: mapPicIdToLabel(repairDetail?.femuschworepairpicId) || ""
                                },
                                activity: {
                                    id: repairDetail?.femuschworepairactivityId || "",
                                    label: mapActivityIdToLabel(repairDetail?.femuschworepairactivityId) || ""
                                },
                                startDateandTime: repairDetail?.repairstartdate || "",
                                endDateandTime: repairDetail?.repairenddate || "",
                                repairDuration: repairDetail?.repairduration || "",
                                remarks: repairDetail?.remarks || "",
                                preparationHours: repairDetail?.preparationhours || "",
                                verificationHours: repairDetail?.verificationhours || "",
                                id: repairDetail?.id || "",
                                technician: repairDetail?.vendortechnician || "",
                                timeStamp: repairDetail?.timestamp || ""
                            };
                        }) || [];


                        const itemCodeResponse = await riRepository.fetchItemCodeoptions(serviceType, categoryType);
                        const mapItemCode = (id) => {
                            const ic = itemCodeResponse?.find(ic => ic?.id === id);
                            return ic ? ic?.itemcode : '';
                        };

                        const maptoUnitCost = (id) => {
                            const ic = itemCodeResponse?.find(ic => ic?.id === id);
                            return ic ? ic?.unitcost : '';
                        };

                        const mapItemDesc = (id) => {
                            const ic = itemCodeResponse?.find(ic => ic?.id === id);
                            return ic ? ic.descriptionname : '';
                        };

                        // Mapping PartReplacement data
                        const partReplacement = data?.femroutineinspectionheaders?.[0]?.femroutineinspectpartsreplacedetails?.map((partDetails) => {
                            return {
                                itemPartCode: {
                                    id: partDetails?.itemcodeid || "",
                                    label: mapItemCode(partDetails?.itemcodeid) || "",
                                    unitCost:maptoUnitCost(partDetails?.itemcodeid) || "",
                                },
                                itemDescription: mapItemDesc(partDetails?.itemcodeid) || "",
                                quantity: partDetails?.quantityused || "",
                                partCost: parseFloat(partDetails?.partscost || 0)?.toFixed(2) || "",
                                id: partDetails?.id || "",
                                timeStamp: partDetails?.timestamp || "",
                                editmode: true
                            };
                        }) || [];


                        const assetNo = {
                            id: data?.assetId || "",
                            label: data?.assetNo || "",
                            repairType:data?.femworespondtype || ""
                        };

                        // const respondedBy = {
                        //     id: data?.assigneeEmployeeId  || "",
                        //     label: data?.assigneeEmployeename || "",
                        // };

                        const respondedBy = {
                            id: data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.respondedbyid || data?.assigneeEmployeeId  || "",
                            label: mapEmployeeIdToName(data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.respondedbyid || "") ||  data?.assigneeEmployeename || "",
                        };

                        const partsRequired = {
                            id: data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.ispartsrequired === true
                                ? 1
                                : data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.ispartsrequired === false
                                    ? 2
                                    : "",
                            label: data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.ispartsrequired === true
                                ? 'Yes'
                                : data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.ispartsrequired === false
                                    ? 'No'
                                    : ""
                        };

                        const withPartsRequired = getStepper;
                        const withoutPartsRequired = getStepper?.filter((item) => item?.label !== 'Part Replacement');

                        if (partsRequired?.label === 'Yes' || partsRequired?.label === "") {
                            dispatch(updateAssessmentsPartsInitialState("UPDATE_ASSESSMENTS_PARTS_REQUIRED_INITIALSTATE", withPartsRequired));
                        } else {
                            dispatch(updateAssessmentsPartsRequired("UPDATE_ASSESSMENTS_PARTS_REQUIRED_CHANGED", withoutPartsRequired));
                        };

                        const assetTakenLocation = {
                            id: data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.isassettakenoutlocation === true
                                ? 1
                                : data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.isassettakenoutlocation === false
                                    ? 2
                                    : "",
                            label: data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.isassettakenoutlocation === true
                                ? 'Yes'
                                : data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.isassettakenoutlocation === false
                                    ? 'No'
                                    : ""
                        };

                        const taskcode = {
                            id: data?.scheduledTaskcodeId || "",
                            label: data?.scheduledTaskcode || ""
                        };

                        const compRespondedBy = {
                            id: data?.completionEmployeeId || "",
                            label: data?.completionEmployeename || "",
                            designation: mapEmployeeIdTodesignation(data?.completionEmployeeId) || ""

                        };

                        const vendor = {
                            id: data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.vendorid || data?.vendorId || "",
                            label: maptoVendorDetails(data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.vendorid) || data?.vendorname || "",
                            vendorcode: maptoVendorCode(data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.vendorid || data?.vendorId ) || ""
                        };

                        if(data?.assetId){
                           setisAssetNoSelected(true);
                        };

                        const mappedData = {
                            id: location.state?.id || hyperLinkDecryptData?.id || "",
                            serviceType: editServiceType || "",
                            workOrderNo: data?.workorderno || '',
                            workorderDateandTime: data?.workorderdate || "",
                            targetDate:data?.targetDate || "",
                            contactNo: data?.femroutineinspectionheaders[0]?.contactno || '',
                            assetNo: assetNo || [],
                            oldAssetNo: data?.femroutineinspectionheaders[0]?.femassetregister?.oldassetno || '',
                            description: data?.assetDescription || '',
                            underwarrenty: data?.femroutineinspectionheaders[0]?.femassetregister?.isunderwarranty === true
                                ? "Yes"
                                : data?.femroutineinspectionheaders[0]?.femassetregister?.isunderwarranty === false
                                    ? "No"
                                    : '',
                            locationCode:data?.locationcode || "",
                            locationDesc: data?.locationdesc || '',
                            deptDesc:data?.departmentdesc || "",
                            workgroupDescription: `${data?.workGroup || ''}${(data?.workGroup && data?.wgdesc) ? " - " : ""}${data?.wgdesc || ''}` || '',
                            workGroupId: data?.wgId || "",
                            requestDetails: data?.femroutineinspectionheaders[0]?.inspectiondetails || '',
                            category: {
                                label: data?.workordercategory || "",
                                isvisible: true || "",
                                id: data?.femroutineinspectionheaders[0]?.femworkordertype?.femworkordercategoryId || ""
                            },
                            type: {
                                label: data?.femroutineinspectionheaders[0]?.femworkordertype?.workordertype || "",
                                isvisible: true,
                                id: data?.femroutineinspectionheaders[0]?.femworkordertype?.id || ""
                            },
                            priority: {
                                label: data?.femroutineinspectionheaders[0]?.femworkorderpriority?.workorderpriority || "",
                                isvisible: true,
                                id: data?.femroutineinspectionheaders[0]?.femworkorderpriority?.id || ""
                            },
                            cancelledBy: data?.femroutineinspectionheaders[0]?.cancelledby || '',
                            reasonforCancellation: data?.femroutineinspectionheaders[0]?.cancellationreason || '',
                            ppmscheduletrackId: data?.femroutineinspectionheaders[0]?.femroutineinspectscheduletrackId || "",
                            targetDate: data?.femroutineinspectionheaders[0]?.targetdate || "",
                            assessments: {
                                id: data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.id || '',
                                respondedBy: respondedBy || '',
                                designation:mapEmployeeIdTodesignation(data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.respondedbyid) || data?.assigneeDesignation  || "",
                                taskCode: taskcode || '',
                                taskDescription: data?.scheduledTaskdesc || '',
                                repairType: data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.femworespondtype?.respondtype || data?.femworespondtype || '',
                                partsRequired: partsRequired || '',
                                assetTakenLocation: assetTakenLocation || '',
                                respondedFinding: data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.respondfinding || '',
                                vendor: vendor || '',
                                outsourceRespondRemarks: data?.femroutineinspectionheaders[0]?.femroutineinspectassesmentheaders?.[0]?.respondremarks || "",
                                repairTracker: repairTracker || [],
                            },
                            completionInfo: {
                                id: data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.id || '',
                                // respondedBy: compRespondedBy || '',
                                designation: data?.completionDesignation || '',
                                duration: data?.duration || '',
                                completionBy: {
                                    id: setvendorField ? vendor?.id : data?.completionByEmployeeId || "",
                                    label: setvendorField ? vendor?.label : data?.completionByEmployeename || "",
                                    value: setvendorField ? vendor?.label : data?.completionByEmployeename || ""
                                },
                                startDateandTime: data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.startdate || '',
                                endDateandTime: data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.enddate || '',
                                actionTaken: data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.actiontaken || '',
                                signatureFiles: [],
                                timeStamp: data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.timeStamp || "",
                                costInfo: {
                                    editmode: true,
                                    breakDownCost: data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.breakdowncost ? parseFloat(data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.breakdowncost || 0)?.toFixed(2) : 0,
                                    poNumber: data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.breakdowncostpurorderno || "",
                                    ppmCostByPurchaseOrder: data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.ppmcost ? parseFloat(data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.ppmcost || 0)?.toFixed(2) : 0,
                                    poNumberByPurchaseOrder: data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.ppmcostpurorderno || "",
                                    ppmCostByContract: data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.contractppmcost ? parseFloat(data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.contractppmcost || 0)?.toFixed(2) : 0,
                                    contractReferenceNumber: data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.contractrefno || "",
                                    totalSparePartCost: data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.sparepartscost ? parseFloat(data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.sparepartscost || 0)?.toFixed(2) : 0,
                                    totalWorkorderCost: data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.totalwocost ? parseFloat(data?.femroutineinspectionheaders[0]?.femroutineinspectcompletiondetails?.[0]?.totalwocost || 0)?.toFixed(2) : 0,
                                }
                            },
                            partReplacementRows: partReplacement || [],
                            Authorizedby: data?.authorizedby || "",
                            AuthorizedbyDate: data?.authorizedbyDate || "",
                            timeStamp: location?.state?.timestamp || '',
                            editmode: true
                        };
                        
                        setBasicDetailsData(mappedData);
                        dispatch(updateBasicDetailsRoutineInspection("UPDATE_BASIC_DETAILS_ROUTINE_INSPECTION", { ...mappedData }));
                        dispatch(updateOutsourceDetails("SET_VENDORFIELD_ROUTINE_INSPECTION", setvendorField));

                    } else {
                        
                        const data = response?.[0];
                        const status = data?.bemroutineinspectionheaders[0]?.bemworkorderstatus?.workorderstatus || "";
                        const percentageStatus = data?.bemroutineinspectionheaders[0]?.processingpercentage || "";
                        const getRepairtype = data?.bemroutineinspectionheaders[0]?.bemroutineinspectassesmentheaders?.[0]?.bemworespondtype?.respondtype ? data?.bemroutineinspectionheaders[0]?.bemroutineinspectassesmentheaders?.[0]?.bemworespondtype?.respondtype : data?.bemworespondtype || ''
                        const setvendorField = getRepairtype === 'Outsource' ? true : false;
                        dispatch(updateWorkOrderStatus("STATUS_CHANGES", { status: status, percentage: percentageStatus }))

                        const employeeResponse = await riRepository.fetchEmployeeDetails();
                        const lovOptionsResponse = await riRepository.lovOptions();
                        const vendorDetails = await riRepository.fetchVendorDetails();

                        const mapEmployeeIdToName = (id) => {
                            const employee = employeeResponse?.find(emp => emp?.id === id);
                            return employee ? employee.employeename : '';
                        };

                        const mapEmployeeIdTodesignation = (id) => {
                            const employee = employeeResponse?.find(emp => emp?.id === id);
                            return employee ? employee?.designation : '';
                        };

                        const mapEmployeeIdToCode = (id) => {
                            const employee = employeeResponse?.find(emp => emp?.id === id);
                            return employee ? employee?.employeecode : '';

                        };

                        const mapFlagIdToLabel = (id) => {
                            const flag = lovOptionsResponse?.femuschworepairflags?.find(f => f?.id === id);
                            return flag ? flag?.repairflag : '';
                        };

                        const maptoVendorDetails = (id) => {
                            const vendor = vendorDetails?.find(v => v?.id === id);
                            return vendor ? vendor?.vendorname : '';
                        };

                        const maptoVendorCode = (id) => {
                            const vendor = vendorDetails?.find(v => v?.id === id);
                            return vendor ? vendor?.vendorcode : '';
                        };

                        const mapPicIdToLabel = (id) => {
                            const pic = lovOptionsResponse?.femuschworepairpics?.find(p => p?.id === id);
                            return pic ? pic?.repairpic : '';
                        };

                        const mapActivityIdToLabel = (id) => {
                            const activity = lovOptionsResponse?.femuschworepairactivities?.find(a => a?.id === id);
                            return activity ? activity?.repairactivity : '';
                        };

                        // Mapping repairTracker data
                        const repairTracker = data?.bemroutineinspectionheaders[0]?.bemroutineinspectassesmentheaders?.[0]?.bemroutineinspectrepairdetails?.map((repairDetail) => {
                            return {
                                employeename: {
                                    id: repairDetail?.employeeid || "",
                                    employeename: mapEmployeeIdToName(repairDetail?.employeeid) || "",
                                    employeecode: mapEmployeeIdToCode(repairDetail?.employeeid) || ""
                                },
                                flag: {
                                    id: repairDetail?.bemuschworepairflagId || '',
                                    label: mapFlagIdToLabel(repairDetail?.bemuschworepairflagId) || ''
                                },
                                pic: {
                                    id: repairDetail?.bemuschworepairpicId || '',
                                    label: mapPicIdToLabel(repairDetail?.bemuschworepairpicId) || ''
                                },
                                activity: {
                                    id: repairDetail.bemuschworepairactivityId || '',
                                    label: mapActivityIdToLabel(repairDetail?.bemuschworepairactivityId) || ''
                                },
                                startDateandTime: repairDetail?.repairstartdate || "",
                                endDateandTime: repairDetail?.repairenddate || "",
                                repairDuration: repairDetail?.repairduration || "",
                                remarks: repairDetail?.remarks || '',
                                technician: repairDetail?.vendortechnician || "",
                                preparationHours: repairDetail?.preparationhours || '',
                                verificationHours: repairDetail?.verificationhours || '',
                                id: repairDetail?.id || "",
                                timeStamp: repairDetail?.timestamp || ""
                            };
                        }) || [];

                        const itemCodeResponse = await riRepository.fetchItemCodeoptions(serviceType, categoryType);
                        const mapItemCode = (id) => {
                            const ic = itemCodeResponse?.find(ic => ic?.id === id);
                            return ic ? ic.itemcode : '';
                        };

                        const maptoUnitCost = (id) => {
                            const ic = itemCodeResponse?.find(ic => ic?.id === id);
                            return ic ? ic?.unitcost : '';
                        };

                        const mapItemDesc = (id) => {
                            const ic = itemCodeResponse?.find(ic => ic?.id === id);
                            return ic ? ic?.descriptionname : '';
                        };
                        // Mapping repairTracker data
                        const partReplacement = data?.bemroutineinspectionheaders[0]?.bemroutineinspectpartsreplacedetails?.map((partDetails) => {
                            return {
                                itemPartCode: {
                                    id: partDetails?.itemcodeid || "",
                                    label: mapItemCode(partDetails?.itemcodeid) || "",
                                    unitCost:maptoUnitCost(partDetails?.itemcodeid) || "",
                                },
                                itemDescription: mapItemDesc(partDetails?.itemcodeid) || "",
                                quantity: partDetails?.quantityused || "",
                                partCost: parseFloat(partDetails?.partscost || 0).toFixed(2) || "",
                                id: partDetails?.id || "",
                                editmode: true,
                                timeStamp: partDetails?.timestamp || "",
                            };
                        }) || [];

                        const assetNo = {
                            id: data?.assetId || "",
                            label: data?.assetNo || "",
                            repairType:data?.bemworespondtype || ""
                        };

                        const respondedBy = {
                            id: data?.bemroutineinspectionheaders[0]?.bemroutineinspectassesmentheaders?.[0]?.respondedbyid || data?.assigneeEmployeeId  || "",
                            label: mapEmployeeIdToName(data?.bemroutineinspectionheaders[0]?.bemroutineinspectassesmentheaders?.[0]?.respondedbyid || "") ||  data?.assigneeEmployeename || "",
                        };

                        const taskcode = {
                            id: data?.scheduledTaskcodeId || "",
                            label: data?.scheduledTaskcode || ""
                        };

                        const partsRequired = {
                            id: data?.bemroutineinspectionheaders?.[0]?.bemroutineinspectassesmentheaders?.[0]?.ispartsrequired === true
                                ? 1
                                : data?.bemroutineinspectionheaders?.[0]?.bemroutineinspectassesmentheaders?.[0]?.ispartsrequired === false
                                    ? 2
                                    : "",
                            label: data?.bemroutineinspectionheaders?.[0]?.bemroutineinspectassesmentheaders?.[0]?.ispartsrequired === true
                                ? 'Yes'
                                : data?.bemroutineinspectionheaders?.[0]?.bemroutineinspectassesmentheaders?.[0]?.ispartsrequired === false
                                    ? 'No'
                                    : ""
                        };
                        const withPartsRequired = getStepper;
                        const withoutPartsRequired = getStepper?.filter((item) => item?.label !== 'Part Replacement');

                        if (partsRequired?.label === 'Yes' || partsRequired?.label === "") {
                            dispatch(updateAssessmentsPartsInitialState("UPDATE_ASSESSMENTS_PARTS_REQUIRED_INITIALSTATE", withPartsRequired));
                        } else {
                            dispatch(updateAssessmentsPartsRequired("UPDATE_ASSESSMENTS_PARTS_REQUIRED_CHANGED", withoutPartsRequired));
                        };

                        const assetTakenLocation = {
                            id: data?.bemroutineinspectionheaders?.[0]?.bemroutineinspectassesmentheaders?.[0]?.isassettakenoutlocation === true
                                ? 1
                                : data?.bemroutineinspectionheaders?.[0]?.bemroutineinspectassesmentheaders?.[0]?.isassettakenoutlocation === false
                                    ? 2
                                    : "",
                            label: data?.bemroutineinspectionheaders?.[0]?.bemroutineinspectassesmentheaders?.[0]?.isassettakenoutlocation === true
                                ? 'Yes'
                                : data?.bemroutineinspectionheaders?.[0]?.bemroutineinspectassesmentheaders?.[0]?.isassettakenoutlocation === false
                                    ? 'No'
                                    : ""
                        };

                        const compRespondedBy = {
                            id: data?.completionEmployeeId || "",
                            label: data?.completionEmployeename || "",
                            designation: mapEmployeeIdTodesignation(data?.completionEmployeeId) || ""
                        };

                        const vendor = {
                            id: data?.bemroutineinspectionheaders?.[0]?.bemroutineinspectassesmentheaders?.[0]?.vendorid || data?.vendorId || "",
                            label: maptoVendorDetails(data?.bemroutineinspectionheaders?.[0]?.bemroutineinspectassesmentheaders?.[0]?.vendorid) || data?.vendorname || "",
                            vendorcode:maptoVendorCode(data?.bemroutineinspectionheaders?.[0]?.bemroutineinspectassesmentheaders?.[0]?.vendorid || data?.vendorId) || ""
                        };


                        const mappedData = {
                            id: location.state?.id || hyperLinkDecryptData?.id || "",
                            serviceType: editServiceType || "",
                            workOrderNo: data?.workorderno || '',
                            workorderDateandTime: data?.workorderdate || "",
                            targetDate:data?.targetDate || "",
                            contactNo: data?.bemroutineinspectionheaders[0]?.contactno || '',
                            assetNo: assetNo || [],
                            oldAssetNo: data?.bemroutineinspectionheaders[0]?.bemassetregister?.oldassetno || '',
                            description: data?.assetDescription || '',
                            underwarrenty: data?.bemroutineinspectionheaders[0]?.bemassetregister?.isunderwarranty === true
                                ? "Yes"
                                : data?.bemroutineinspectionheaders[0]?.bemassetregister?.isunderwarranty === false
                                    ? "No"
                                    : '',
                            locationCode:data?.locationcode || "",
                            locationDesc: data?.locationdesc || '',
                            deptDesc:data?.departmentdesc || "",
                            workgroupDescription: `${data?.workGroup || ''}${(data?.workGroup && data?.wgdesc) ? " - " : ""}${data?.wgdesc || ''}` || '',
                            workGroupId: data?.wgId || "",
                            requestDetails: data?.bemroutineinspectionheaders[0]?.inspectiondetails || '',
                            category: {
                                label: data?.workordercategory || "",
                                isvisible: true || "",
                                id: data?.bemroutineinspectionheaders[0]?.bemworkordertype?.bemworkordercategoryId || ""
                            },
                            type: {
                                label: data?.bemroutineinspectionheaders[0]?.bemworkordertype?.workordertype || "",
                                isvisible: true,
                                id: data?.bemroutineinspectionheaders[0]?.bemworkordertype?.id || ""
                            },
                            priority: {
                                label: data?.bemroutineinspectionheaders[0]?.bemworkorderpriority?.workorderpriority || "",
                                isvisible: true,
                                id: data?.bemroutineinspectionheaders[0]?.bemworkorderpriority?.id || ""
                            },
                            cancelledBy: data?.bemroutineinspectionheaders[0]?.cancelledby || '',
                            reasonforCancellation: data?.bemroutineinspectionheaders[0]?.cancellationreason || '',
                            ppmscheduletrackId: data?.bemroutineinspectionheaders[0]?.bemroutineinspectscheduletrackId || "",
                            targetDate: data?.bemroutineinspectionheaders[0]?.targetdate || "",
                            assessments: {
                                id: data?.bemroutineinspectionheaders[0]?.bemroutineinspectassesmentheaders?.[0]?.id || '',
                                respondedBy: respondedBy || '',
                                designation:mapEmployeeIdTodesignation(data?.bemroutineinspectionheaders[0]?.bemroutineinspectassesmentheaders?.[0]?.respondedbyid || "") || data?.assigneeDesignation  || "",
                                taskCode: taskcode || '',
                                taskDescription: data?.scheduledTaskdesc || '',
                                repairType:data?.bemroutineinspectionheaders[0]?.bemroutineinspectassesmentheaders?.[0]?.bemworespondtype?.respondtype || data?.bemworespondtype || '',
                                partsRequired: partsRequired || '',
                                assetTakenLocation: assetTakenLocation || '',
                                respondedFinding: data?.bemroutineinspectionheaders[0]?.bemroutineinspectassesmentheaders?.[0]?.respondfinding || '',
                                vendor: vendor || '',
                                outsourceRespondRemarks: data?.bemroutineinspectionheaders[0]?.bemroutineinspectassesmentheaders?.[0]?.respondremarks || "",
                                repairTracker: repairTracker || [],
                            },
                            completionInfo: {
                                id: data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.id || '',
                                // respondedBy: compRespondedBy || '',
                                designation: data?.completionDesignation || '',
                                duration: data?.duration || '',
                                completionBy: {
                                    id: setvendorField ? vendor?.id : data?.completionByEmployeeId || "",
                                    label: setvendorField ? vendor?.label : data?.completionByEmployeename || "",
                                    value: setvendorField ? vendor?.label : data?.completionByEmployeename || ""
                                },
                                startDateandTime: data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.startdate || '',
                                endDateandTime: data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.enddate || '',
                                actionTaken: data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.actiontaken || '',
                                signatureFiles: [],
                                timeStamp: data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.timeStamp || "",
                                costInfo: {
                                    editmode: true,
                                    breakDownCost: data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.breakdowncost ? parseFloat(data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.breakdowncost || 0)?.toFixed(2) : 0,
                                    poNumber: data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.breakdowncostpurorderno || "",
                                    ppmCostByPurchaseOrder: data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.ppmcost ? parseFloat(data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.ppmcost || 0)?.toFixed(2) : 0,
                                    poNumberByPurchaseOrder: data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.ppmcostpurorderno || "",
                                    ppmCostByContract: data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.contractppmcost ? parseFloat(data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.contractppmcost || 0)?.toFixed(2) : 0,
                                    contractReferenceNumber: data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.contractrefno || "",
                                    totalSparePartCost: data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.sparepartscost ? parseFloat(data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.sparepartscost || 0)?.toFixed(2) : 0,
                                    totalWorkorderCost: data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.totalwocost ? parseFloat(data?.bemroutineinspectionheaders[0]?.bemroutineinspectcompletiondetails?.[0]?.totalwocost || 0)?.toFixed(2) : 0,
                                }
                            },
                            partReplacementRows: partReplacement || [],
                            Authorizedby: data?.authorizedby || "",
                            AuthorizedbyDate: data?.authorizedbyDate || "",
                            timeStamp: location?.state?.timestamp || '',
                            editmode: true
                        };

                        setBasicDetailsData(mappedData);
                        dispatch(updateBasicDetailsRoutineInspection("UPDATE_BASIC_DETAILS_ROUTINE_INSPECTION", { ...mappedData }));
                        dispatch(updateOutsourceDetails("SET_VENDORFIELD_ROUTINE_INSPECTION", setvendorField));
                    };
            };
        } catch (error) {
            console.error("Error fetching data:", error);
        }
        
        if(location?.state?.id || hyperLinkDecryptData?.id){
            setLoading(false);
        }
    };

    useEffect(()=>{
            getDecryptData()
    
        },[])

    useEffect(() => {

        async function initializeData() {
                    setLoading(true);
                    await Promise.all([
                        assetNoOptions(),
                        categoryandPriorityOptions(),
                    ]);

                    if (basicDetails?.id) {
                        setBasicDetailsData(basicDetails);
                    }
        
                    // setLoading(false);
                }

        initializeData();
        fetchData();
    }, [hyperLinkDecryptData,dispatch]);

    useEffect(() => {
        const fetchAssetNo = async () => {
            await assetNoOptions();
        }
        fetchAssetNo();
    }, [basicDetailsdata?.referenceWorkRequestNo, location?.state?.editmode])

    const commonSubmitFunction = async () => {
        // setLoading(true);
        const isValid = handleValidationForRequiredFields();

        if (isValid) {
            let commonSubmitResponse = null;
            const API_Payload = {
                routineinspectionno: basicDetailsdata?.workOrderNo || "",
                routineinspectiondate: basicDetailsdata?.workorderDateandTime || "",
                targetdate: basicDetailsdata?.targetDate || "",
                inspectiondetails: basicDetailsdata?.requestDetails || "",
                cancelledby: basicDetailsdata?.cancelledBy || "",
                cancellationreason: basicDetailsdata?.reasonforCancellation || "",
                completeddate: basicDetailsdata?.workorderDateandTime || "",
                processingpercentage: basicDetails?.editmode ? processingPercentage : 16 || "",
                [`${serviceType}assetregisterId`]: basicDetailsdata?.assetNo?.id || "",
                [`${serviceType}workordertypeId`]: basicDetailsdata?.type?.id || "",
                [`${serviceType}workorderpriorityId`]: basicDetailsdata?.priority?.id || "",
                [`${serviceType}workorderstatusId`]: basicDetails?.editmode ? mappedStatus : 2 || "", //1 for  open status id while creating the work order
                [`${serviceType}routineinspectscheduletrackId`]: basicDetails?.ppmscheduletrackId || "", //1 for  open status id while creating the work order
                id: basicDetailsdata?.id || "",
            };

            if (basicDetails?.id) {
                const updateWorkOrderResponse = await riRepository.updateBasicDetails(serviceType, API_Payload, categoryType);

                commonSubmitResponse = updateWorkOrderResponse?.id;
                if (updateWorkOrderResponse?.id) {
                    setPageNavigate(true);
                    dispatch(updateBasicDetailsRoutineInspection("UPDATE_BASIC_DETAILS_ROUTINE_INSPECTION", { ...basicDetailsdata, id: updateWorkOrderResponse?.id, workOrderNo: updateWorkOrderResponse?.routineinspectionno, isverify:location?.state?.isverify || "" , isresponse:location?.state?.isresponse || "" }));
                } else if (updateWorkOrderResponse?.response?.data?.status >= 405) {
                    setErrorDialog(true);
                    setErrorDialogMssg("An unexpected error occurred while processing your request.")
                }
            }
            return !!commonSubmitResponse;
        };
        return false;
    };
    

    useEffect(() => {

        if (pageNavigate && !checkAutoAsign) {
            // setLoading(true);
            setPage("Assessments");
            dispatch(mandatoryFields(false, "UPDATE_MANDATORY_REQUIRED_ENABLED"));
            setPageNavigate(false);
            setLoading(false)
        };

    }, [basicDetailsdata, basicDetails?.id, pageNavigate, checkAutoAsign]);

    const handleSubmit = async (e) => {
        setLoading(true);
        e.preventDefault();
        if(!viewmode && mappedStatus !== 6){
            await commonSubmitFunction();
            if (pageNavigate) {
                setPage("Assessments");
                dispatch(mandatoryFields(false, "UPDATE_MANDATORY_REQUIRED_ENABLED"));
            };
        }else{
            setPage("Assessments");
            dispatch(mandatoryFields(false, "UPDATE_MANDATORY_REQUIRED_ENABLED"));
        }
        
        setLoading(false);
    };

    const onEnabledChange = (name) => {
        if (name === "yes") {
            setEnable(true)
        }
        else if (name === "no") {
            setEnable(false)
        };
    };

    const handleConfirmCancelWO = async (updatedValues) => {
        setLoading(true);
        const isValid = handleValidationForRequiredFields();
        isValid ? (() => {
            onEnabledChange("yes");
            setBasicDetailsData((prev) => ({
                ...prev,
                cancelledBy: updatedValues.cancelledBy,
                reasonforCancellation: updatedValues.reasonforCancellation,
            }));
        })()
            : onEnabledChange("no");

        const cancelWorkOrderPayload = {
            workorderno: basicDetailsdata?.workOrderNo || "",
            workorderdate: basicDetailsdata?.workorderDateandTime || "",
            targetdate: categoryType === "Scheduled" ?
                basicDetailsdata?.workorderDateandTime : (basicDetailsdata?.issueDateandTimeBasicDetails
                    ? basicDetailsdata?.issueDateandTimeBasicDetails : basicDetailsdata?.workorderDateandTime),
            requestor: basicDetailsdata?.requestor || "",
            designation: basicDetailsdata?.designation || "",
            receiveddate: basicDetailsdata?.recivedDateandTime || "",
            contactno: basicDetailsdata?.contactNo || "",
            requestdetails: basicDetailsdata?.requestDetails || "",
            receivedbyid: basicDetailsdata?.receivedbyid || "",
            cancelledby: updatedValues?.cancelledBy || "",
            cancellationreason: updatedValues?.reasonforCancellation,
            isppmused: basicDetailsdata?.isppmused || false,
            [`${serviceType}assetregisterId`]: basicDetailsdata?.assetNo?.id || "",
            [`${serviceType}workorderstatusId`]: 6,//send ID for Cancel Workorder Status
            [`${serviceType}workordertypeId`]: basicDetailsdata?.type?.id || "",
            [`${serviceType}workorderpriorityId`]: basicDetailsdata?.priority?.id || "",
            [`${serviceType}workordersafetyperformtestId`]: basicDetailsdata?.femworkordersafetyperformtestId || "",
            [`${serviceType}wotargetresponseId`]: basicDetailsdata?.priority?.id || "",
            completeddate: basicDetailsdata?.completeddate || "",
            [`${serviceType}departmentworkrequestheaderId`]: basicDetailsdata?.referenceWorkRequestNo?.id || "",
            [`${serviceType}ppmscheduletrackId`]: basicDetails?.ppmscheduletrackId || "",
            id: basicDetailsdata?.id || "",
            Processingpercentage: processingPercentage || "",
            timestamp: basicDetails?.timeStamp || "",
            isverify:location?.state?.isverify === "Yes" ? 1 : ""
        };

        if (isValid) {
            const cancelWorkOrder = await riRepository.updateBasicDetails(serviceType, cancelWorkOrderPayload, categoryType);
            if (cancelWorkOrder?.id) {
                dispatch(updateBasicDetailsRoutineInspection("UPDATE_BASIC_DETAILS_ROUTINE_INSPECTION", { ...basicDetailsdata, id: cancelWorkOrder?.id }));
                dispatch(updateWorkOrderStatus("STATUS_CHANGES", { status:statusList?.cancelled?.label, percentage: processingPercentage }));
                dispatch(mandatoryFields(false, "UPDATE_MANDATORY_REQUIRED_ENABLED"));
            } else if (cancelWorkOrder?.response?.data?.status >= 405) {
                setErrorDialog(true);
                setErrorDialogMssg("An unexpected error occurred while processing your request.")
            }
        };
        setLoading(false);
    };

    const handleConfirmforWOandAutoAsign = async () => {
        setLoading(true);
        const isValid = handleValidationForRequiredFields();
        if (isValid) {

            const response = await commonSubmitFunction();
            setConfirmationAutoAsign(false);
            if (response) {
                setTimeout(() => {
                    setOpenAutoAsignDialog(true);
                }, 500);
            };

        };
        setLoading(false)
    };


    const handleOpenconfirmDialog = () => {
        const isValid = handleValidationForRequiredFields();
        if (isValid) {
            setCheckAutoAsign(true);
            setConfirmationAutoAsign(true);
        };
    };

    const handleCloseAutoasignDialog = () => {
        setOpenAutoAsignDialog(false);
    };

    const handleCloseErrRequestDialog = () => {
        setErrorDialog(false);
        setErrorDialogMssg("");
    };

    const handleCloseConfirmationDialog = () => {
        setConfirmationAutoAsign(false);
        setCheckAutoAsign(false);
    };

    const handleConfirmAutoAsign = (e) => {
        e.preventDefault();
        setOpenAutoAsignDialog(false);
    };

    const handleConfirmErrRequestDialog = (e) => {
        e.preventDefault();
        setErrorDialog(false);
        setErrorDialogMssg("")
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleAssetClick = (rowData) => {
        setBasicDetailsData((prev) => ({
            ...prev,
            assetNo: { ...rowData, label: rowData?.assetNo ? rowData?.assetNo : '' },
            oldAssetNo: rowData?.oldAssetNo || "",
            underwarrenty: rowData?.underwarrenty || "",
            description: rowData?.assetdesc || "",
            locationCode: rowData?.locationCode || "",
            locationDesc: rowData?.locationDesc || "",
            workgroupDescription: rowData?.workGroupcodeDesc || "",
            workGroupId: rowData?.workGroupId || ""
        }));

        setError((prev) => ({
            ...prev,
            assetNo: false

        }));

        setErrorMessage((prev) => ({
            ...prev,
            assetNo: ''
        }));

        // Close the modal after asset selection
        handleCloseModal();
    };

    const columns = [
        {
            headerName: 'Asset No',
            field: 'assetNo',
            width: 150,
            cellRenderer: (params) => (
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        handleAssetClick(params.data);
                    }}
                    style={{
                        color: 'blue',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                    }}
                >
                    {params.value}
                </a>
            ),
        },
        { headerName: 'Description', field: 'assetdesc', width: 200 },
        { headerName: 'Service', field: 'serviceName', width: 100 },
        { headerName: 'Old Asset No', field: 'oldAssetNo', width: 150 },
        { headerName: 'Under Warranty', field: 'underwarrenty', width: 150 },
        { headerName: 'Location Code', field: 'locationCode', width: 150 },
        { headerName: 'Location Description', field: 'locationDesc', width: 150 }, 
        { headerName: 'Work Group & Description', field: 'workGroupcodeDesc', width: 150 }
    ];

    const formatDate = (dateString) => {
    
        if (!dateString) return ""; // Handle empty or undefined dates
        const date = new Date(dateString);
    
        const day = String(date.getDate()).padStart(2, '0'); // Ensure 2 digits
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
        const year = date.getFullYear();
    
        const formattedDate = `${day}/${month}/${year}`;
    
        return formattedDate; // Formats to DD/MM/YYYY
    };
    

    return (

        <>
            <PageLoader open={loading} />
            <ComponentHeader
                mainHeader={findHeaderContent?.mainHeader}
                subHeader={findHeaderContent?.subHeader}
                secondSubHeaderAndDate={findHeaderContent?.secondSubHeaderAndDate}
                requiredButton={viewmode ? false : findHeaderContent?.requiredButton}
            />

            <Box mt={{ xs: 1, md: 2 }} sx={{ marginTop: '0rem' }}>
                <form style={{ position: "relative" }} onSubmit={(e) => handleSubmit(e)} noValidate>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4} sm={6}>
                            <CustomTextField
                                label="Work Order No(Auto)"
                                id="workOrderNo"
                                name="workOrderNo"
                                value={basicDetailsdata?.workOrderNo || ""}
                                onChange={handleOnchange}
                                disabled={true}
                            />
                        </Grid>
                        <Grid item xs={6} md={4} sm={6}>
                            <CustomDateTimePicker
                                label="Work Order Date and Time"
                                value={basicDetailsdata?.workorderDateandTime || ""}
                                onDateChange={(e) => handleOnchange(e, "workorderDateandTime")}
                                helperText={errorMessage?.workorderDateandTime}
                                error={error?.workorderDateandTime}
                                isMandatoryField={true}
                                disableFutureDate={false}
                                disabled={true}
                            />
                        </Grid>
                        <Grid item xs={6} md={4} sm={6}>
                            <CustomDatePicker
                                label="Target Date"
                                value={formatDate(basicDetailsdata?.targetDate) || ""} 
                                onDateChange={(e) => handleOnchange(e, "targetDate")}
                                disableFutureDate={false}
                                disabled={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={4} sm={6}>
                            <CustomTextField
                                label="Contact No."
                                id="contactNo"
                                name="contactNo"
                                value={basicDetailsdata?.contactNo || ""}
                                onChange={handleOnchange}
                                maxlength={20}
                                disabled={viewmode || mappedStatus === 6}
                            // disabled={isAssetNoSelected || viewmode || mappedStatus === 4 || mappedStatus === 5 || mappedStatus === 6 || mappedStatus === 8}
                            />
                        </Grid>
                        <Grid item xs={12} md={4} sm={6}>
                            {/* <Grid item xs={(viewmode || setScheduledField) ? 12 : 11.5} md={(viewmode || setScheduledField) ? 4 : 3.5} sm={(viewmode || setScheduledField) ? 6 : 5.5}> */}
                            <CustomTextFieldWithSearch
                                label="Asset No."
                                id="assetno"
                                name="assetNo"
                                value={basicDetailsdata?.assetNo || ""}
                                onChange={handleOnchange}
                                error={error?.assetNo}
                                options={basicDetailsOptions?.assetNo || []}
                                errorMessage={errorMessage?.assetNo}
                                hideSearch={true}
                                disabled={true}
                            // disabled={ viewmode || mappedStatus === 4 || mappedStatus === 5 || mappedStatus === 6 || mappedStatus === 8 || isLocationCodeSelecetd}
                            />
                        </Grid>

                        {/* {(!viewmode) && (<Grid item xs={0.5} md={0.5} sm={0.5} >

                            {!viewmode && (
                                <Grid sx={{ mt: 2.7 }} item>
                                    <Button
                                        onClick={handleOpenModal}
                                        variant="contained"
                                        sx={{ minWidth: '32px', minHeight: '32px', marginLeft: '0rem' }}
                                        disabled={viewmode || mappedStatus === 6}>
                                        {/* disabled={ viewmode || mappedStatus === 4 || mappedStatus === 5 || mappedStatus === 6 || mappedStatus === 8}> 
                                        <SearchIcon fontSize="small" />
                                    </Button>
                                </Grid>
                            )}

                            <Dialog
                                open={isModalOpen}
                                onClose={(event, reason) => {
                                    if (reason === "backdropClick") {
                                        return; // Prevent closing on backdrop click
                                    }
                                    handleCloseModal(); // Call the close function for other reasons
                                }}
                                // onClose={handleCloseModal}
                                aria-labelledby="asset-grid-dialog"
                                aria-describedby="grid-display-for-assets"
                                maxWidth="lg"
                                fullWidth
                            ><DialogTitle
                                id="asset-grid-dialog"
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '1.5rem', // Adjust font size as desired
                                    display: 'flex',
                                    justifyContent: 'space-between', // Space out title and icon
                                    alignItems: 'center',
                                }}
                            >
                                    Asset Search
                                    <IconButton
                                        aria-label="close"
                                        variant="outlined"
                                        onClick={handleCloseModal}
                                        sx={{
                                            color: (theme) => theme.palette.grey[500],
                                        }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </DialogTitle>
                                <DialogContent
                                    dividers
                                    sx={{
                                        overflowY: 'auto', // Enables scroll if content overflows
                                        height: 'auto',    // Allows height to adjust based on content
                                        maxHeight: 'auto', // Sets a maximum height to prevent it from growing too large
                                    }}
                                >
                                    <CustomAgGrid
                                        rows={basicDetailsOptions?.assetNo || []}
                                        columns={columns}
                                        // loading={loadingAssets}
                                        refSet={gridApi}
                                        setGridApi={setGridApi}
                                    />
                                    <Stack my={3} direction={"row"} justifyContent={"end"}>
                                        <Button variant="outlined" onClick={handleCloseModal}>
                                            Close
                                        </Button>
                                    </Stack>
                                </DialogContent>
                            </Dialog>

                        </Grid>)} */}

                        <Grid item xs={12} md={4} sm={6}>
                            <CustomTextField
                                label="Old Asset No."
                                id="oldAssetNo"
                                name="oldAssetNo"
                                value={basicDetailsdata?.oldAssetNo || ""}
                                onChange={handleOnchange}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} md={4} sm={6}>
                            <CustomTextField
                                label="Description"
                                id="description"
                                name="description"
                                value={basicDetailsdata?.description || ""}
                                onChange={handleOnchange}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} md={4} sm={6}>
                            <CustomTextField
                                label="Under Warranty"
                                id="underwarrenty"
                                name="underwarrenty"
                                value={basicDetailsdata?.underwarrenty || ""}
                                onChange={handleOnchange}
                                disabled
                            />
                        </Grid>
                        {/* <Grid item xs={12} md={4} sm={6}>
                            <CustomTextField
                                label="Location"
                                id="location"
                                name="location"
                                value={basicDetailsdata?.location || ""}
                                onChange={handleOnchange}
                                disabled
                            />
                        </Grid> */}
                        <Grid item xs={12} md={4} sm={6}>
                            <CustomTextField
                                label="Work Group & Description"
                                id="workgroupDescription"
                                name="workgroupDescription"
                                value={basicDetailsdata?.workgroupDescription || ""}
                                onChange={handleOnchange}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} md={4} sm={6}>
                        {/* <CustomTextFieldWithSearch
                                label="Location Code"
                                id="locationCode"
                                name="locationCode"
                                value={basicDetailsdata?.locationCode || ""}
                                onChange={handleOnchange}
                                error={error?.locationCode}
                                options={basicDetailsOptions?.locationCode || []}
                                errorMessage={errorMessage?.locationCode}
                                isMandatoryField={isAssetNoSelected ? false : true}
                                disabled={isAssetNoSelected || viewmode || mappedStatus === 6}
                            /> */}
                            <CustomTextField
                                label="Location Code"
                                id="locationCode"
                                name="locationCode"
                                value={basicDetailsdata?.locationCode || ""}
                                onChange={handleOnchange}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} md={4} sm={6}>
                            <CustomTextField
                                label="Location Description"
                                id="locationDescription"
                                name="locationDescription"
                                value={basicDetailsdata?.locationDesc || ""}
                                onChange={handleOnchange}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} md={4} sm={6}>
                            <CustomTextField
                                label="Department Description"
                                id="deptDescription"
                                name="deptDescription"
                                value={basicDetailsdata?.deptDesc || ""}
                                onChange={handleOnchange}
                                disabled
                            />
                        </Grid>
                    </Grid>
                    <Box mt={{ xs: 1, md: 2 }} sx={{ marginTop: '0rem' }}>
                        <Typography variant='title2' > RI Details </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={8} sm={8}>
                                <CustomTextArea
                                    label="Routine Inspection Details"
                                    id="requestDetails"
                                    value={basicDetailsdata?.requestDetails || ""}
                                    onChange={(e) => handleOnchange(e, "requestDetails", "Request Details")}
                                    error={error?.requestDetails}
                                    isMandatoryField={true}
                                    helperText={errorMessage?.requestDetails}
                                    disabled={viewmode || mappedStatus === 6}
                                    // disabled={ viewmode || mappedStatus === 4 || mappedStatus === 5 || mappedStatus === 6 || mappedStatus === 8}
                                    maxLength={255}
                                />
                            </Grid>
                        </Grid>
                    </Box>


                    <Box mt={{ xs: 1, md: 2 }} sx={{ marginTop: '0rem' }}>
                        <Typography variant='title2' >Work Order Receipt and Classification</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4} sm={6}>
                                <CustomSelectInput
                                    label="Category"
                                    id="Category"
                                    name="category"
                                    onChange={handleOnchange}
                                    value={basicDetailsdata?.category || ""}
                                    options={basicDetailsOptions?.category || []}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item xs={12} md={4} sm={6}>
                                <CustomSelectInput
                                    label="Type"
                                    id="type"
                                    name="type"
                                    options={basicDetailsOptions?.type || []}
                                    value={basicDetailsdata?.type || []}
                                    onChange={handleOnchange}
                                    disabled={true}
                                // disabled={isAssetNoSelected || viewmode || mappedStatus === 4 || mappedStatus === 5 || mappedStatus === 6 || mappedStatus === 8 || setScheduledField}
                                />
                            </Grid>
                            <Grid item xs={12} md={4} sm={6}>
                                <CustomSelectInput
                                    label="Priority"
                                    id="priority"
                                    name="priority"
                                    value={basicDetailsdata?.priority || []}
                                    onChange={handleOnchange}
                                    options={basicDetailsOptions?.priority || []}
                                    disabled={viewmode || mappedStatus === 6}
                                // disabled={viewmode || mappedStatus === 4 || mappedStatus === 5 || mappedStatus === 6 || mappedStatus === 8}
                                />
                            </Grid>

                            {checkcancelWOStatus && (
                                <>
                                    <Grid item xs={12} md={4} sm={6}>
                                        <CustomTextField
                                            label="Cancelled By"
                                            id="cancelledBy"
                                            name="cancelledBy"
                                            value={basicDetailsdata?.cancelledBy || ""}
                                            onChange={handleOnchange}
                                            disabled={true}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>

                    </Box>

                    {checkcancelWOStatus && (
                        <Box mt={{ xs: 1, md: 2 }} sx={{ marginTop: '0rem' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4} sm={4}>
                                    <CustomTextArea
                                        label="Reason for Cancellation"
                                        id="reasonforCancellation"
                                        value={basicDetailsdata?.reasonforCancellation || ""}
                                        onChange={(e) => handleOnchange(e, "reasonforCancellation")}
                                        disabled={true}

                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}


                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            justifyContent: { xs: "center", md: "flex-end" },
                            mt: 3,
                        }}
                    >
                        <RoutineInspectionBottomButtons page={page} buttonAttributes={buttonAttributes} cancelWo={viewmode || !(access?.canCancel) ? false : checkcancelWOStatus} onEnableChange={onEnabledChange} enabel={enable} enableAuthorizeButton={enable} onConfirm={handleConfirmCancelWO} saveButton={location?.state?.editmode || location?.state?.viewmode ? false : true} saveButtonName={"Create"} disableSave={basicDetails?.id ? true : false} saveClick={handleOpenconfirmDialog} />
                    </Box>

                </form>

                <CustomAlert
                    open={openAutoAsignDialog}
                    onClose={handleCloseAutoasignDialog}
                    onConfirm={handleConfirmAutoAsign}
                    content={`${basicDetails?.workOrderNo || ""} has been Created Successfully and Auto Assign initiated.`}
                    confirmBtn='OK'
                />

                <CustomAlert
                    open={errorDialog}
                    onClose={handleCloseErrRequestDialog}
                    onConfirm={handleConfirmErrRequestDialog}
                    content={errorDialogMssg}
                    confirmBtn='OK'
                />

                <CustomDialog
                    open={confirmationAutoAsign}
                    onClose={handleCloseConfirmationDialog}
                    onConfirm={handleConfirmforWOandAutoAsign}
                    content="Are you sure you want to Create Work Order and Auto Assign?"
                    cancelBtn="No"
                    confirmBtn="Yes"
                />
            </Box>
        </>


    );
};

export default RoutineInspectionBasicDetails;
